<template>
  <div class="ai-insights">
    <h5 class="mb-3">
      <span class="emoji">🔍</span> Inventory Insights
      <span class="text-muted fs-6">(AI powered)</span>
    </h5>

    <!-- USER INPUT FIELD -->
    <div class="user-input-box mb-3">
      <textarea
        v-model="userPrompt"
        class="form-control"
        rows="3"
        placeholder="Ask anything... e.g. 'Predict demand for high-selling items next month'"
      ></textarea>
      <button class="btn btn-primary mt-2 w-100" @click="runInsight" :disabled="loading || !userPrompt">
        {{ loading ? 'Analyzing…' : 'Ask AI' }}
      </button>
    </div>

    <!-- loading skeleton -->
    <div v-if="loading" class="skeleton-card">
      <div class="skeleton-line w-50" />
      <div class="skeleton-line w-80" />
      <div class="skeleton-line w-40" />
    </div>

    <!-- response -->
    <div v-else-if="result" class="answer-card">
      <div class="answer-header">
        <span class="badge bg-primary">User Query</span>
          <button class="btn btn-sm btn-outline-secondary" @click="printResult">
        🖨️ Print
      </button>
        <button class="btn btn-sm btn-link disabled" @click="toggleRaw">
          {{ showRaw ? 'Hide' : 'Show' }} snapshot
        </button>
      </div>

      <div class="answer-body" v-html="renderedMarkdown"></div>

      <transition name="fade">
        <pre v-if="showRaw" class="raw-data">{{ snapshot }}</pre>
      </transition>
    </div>

    <!-- empty -->
    <div v-else class="empty-card">
      <span class="emoji">🤖</span>
      <p>Ask any inventory-related question and the AI will analyze your latest data.</p>
      <p class="amharic">ጥያቄ ይጻፉ — የውሂብ ጥናት ይጀምራል።</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { marked } from 'marked'
import api from '@/api/axios'

const userPrompt = ref('')
const result = ref('')
const snapshot = ref({})
const loading = ref(false)
const showRaw = ref(false)

async function runInsight() {
  loading.value = true
  result.value = ''
  snapshot.value = {}

  try {
    const { data } = await api.post('/ai/full/', { analyzType: userPrompt.value })
    result.value = data.result
    snapshot.value = data.snapshot
  } catch (e) {
    result.value = `❌ ${e.response?.data?.detail || 'Network error'}`
  } finally {
    loading.value = false
  }
}

const renderedMarkdown = computed(() => {
  return result.value ? marked(result.value) : ''
})

function toggleRaw() {
  showRaw.value = !showRaw.value
}

function printResult() {
  const printContent = document.querySelector('.answer-card').innerHTML;
  const originalContent = document.body.innerHTML;

  document.body.innerHTML = printContent;
  window.print();
  document.body.innerHTML = originalContent;
  window.location.reload(); // restore Vue app
}

</script>

<style scoped>
.user-input-box textarea {
  resize: vertical;
  font-size: 0.95rem;
}

/* (keep all your existing styles below — unchanged) */
.ai-insights {
  max-width: 900px;
  margin: 0 auto;
}

/* ---------- chips ---------- */
.prompt-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.chip {
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 2rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}
.chip:hover {
  border-color: #86b7fe;
}
.chip.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

/* ---------- skeleton ---------- */
.skeleton-card {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.skeleton-line {
  height: 1rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: pulse 1.5s infinite;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
}
@keyframes pulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ---------- answer card ---------- */
.answer-card {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  padding: 1.5rem;
}
.answer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.answer-body {
  font-size: 0.95rem;
  line-height: 1.6;
}
.answer-body table {
  margin-top: 0.5rem;
  font-size: 0.85rem;
}
.answer-body :deep(th),
.answer-body :deep(td) {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e9ecef;
}
.answer-body :deep(th) {
  background-color: #f8f9fa;
}

/* ---------- raw data ---------- */
.raw-data {
  margin-top: 1rem;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.4rem;
  font-size: 0.75rem;
  overflow-x: auto;
  max-height: 300px;
}

/* ---------- empty state ---------- */
.empty-card {
  text-align: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: 0.5rem;
  border: 2px dashed #dee2e6;
  color: #6c757d;
}
.empty-card .emoji {
  font-size: 2rem;
}
.empty-card .amharic {
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

/* ---------- transitions ---------- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.date-bar {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  background: #fff;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.date-chip {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.date-chip label {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
}
.date-chip input {
  width: 140px;
}

.answer-card {
  position: relative;
  border: 1px solid rgba(0, 123, 255, 0.4);
  box-shadow: 0px 0px 18px rgba(0, 123, 255, 0.3);
  transition: all 0.4s ease-in-out;
}

.answer-card:hover {
  box-shadow: 0px 0px 24px rgba(0, 123, 255, 0.6);
  border-color: rgba(0, 123, 255, 0.8);
}

</style>
